import { Chess, createPlayableBoard, refreshPlayableBoard } from './chess-ui.js';

const root = document.querySelector('[data-puzzle-root]');
if (root) mountPuzzleStudio(root);

function mountPuzzleStudio(root) {
  const boardElement = root.querySelector('#puzzleBoard');
  const status = root.querySelector('[data-puzzle-status]');
  const progress = root.querySelector('[data-puzzle-progress]');
  const resultCard = root.querySelector('[data-puzzle-result]');
  const ratingElement = document.querySelector('[data-puzzle-rating]');
  const attemptsElement = document.querySelector('[data-puzzle-attempts]');
  const loadingCover = root.querySelector('[data-puzzle-loading]');
  const nextButton = root.querySelector('[data-puzzle-next]');
  const resetButton = root.querySelector('[data-puzzle-reset]');
  const answerButton = root.querySelector('[data-puzzle-answer]');
  const undoButton = root.querySelector('[data-puzzle-undo]');
  const redoButton = root.querySelector('[data-puzzle-redo]');
  const actionPanel = root.querySelector('.puzzle-board-actions');
  const categoryButtons = [...document.querySelectorAll('[data-puzzle-category]')];
  const game = new Chess(root.dataset.fen);
  const turnStatus = () => game.turn() === 'w' ? 'نوبت سفید' : 'نوبت سیاه';
  let initialFen = root.dataset.fen;
  let solutionLines = readSolutionLines(root.dataset.solutions);
  let selectedCategory = root.dataset.category || '';
  let cursor = 0;
  let attemptRecorded = false;
  let lastAttemptResponse = null;
  let locked = false;
  let completed = false;
  let showingAnswer = false;
  let timeline = [];
  let timelineIndex = -1;
  let loading = false;
  let playedMoves = [];
  let candidates = cloneSolutions(solutionLines);
  let activeAnswer = candidates[0] ?? [];

  let puzzleOrientation = orientationFor(game);
  const ground = createPlayableBoard(boardElement, game, onUserMove, {
    orientation: puzzleOrientation,
    enabled: true,
    refreshAfterMove: false
  });

  resetButton?.addEventListener('click', reset);
  answerButton?.addEventListener('click', showAnswer);
  undoButton?.addEventListener('click', () => navigateTimeline(-1));
  redoButton?.addEventListener('click', () => navigateTimeline(1));
  nextButton?.addEventListener('click', () => loadNext(selectedCategory));
  categoryButtons.forEach(button => button.addEventListener('click', () => {
    const category = button.dataset.puzzleCategory || '';
    if (loading || category === selectedCategory) return;
    selectedCategory = category;
    updateCategoryButtons();
    void loadNext(category, true);
  }));
  document.addEventListener('keydown', event => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    if (event.key.toLowerCase() === 'r') reset();
    if (event.key.toLowerCase() === 'n') void loadNext(selectedCategory);
    if (event.key === 'ArrowLeft') navigateTimeline(-1);
    if (event.key === 'ArrowRight') navigateTimeline(1);
  });

  renderReadyState();

  async function onUserMove(move) {
    if (locked || completed || showingAnswer || loading || timelineIndex < timeline.length - 1) {
      game.undo();
      refreshPlayableBoard(ground, game, false);
      return;
    }

    const uci = toUci(move);
    const matching = candidates.filter(line => line[cursor] === uci);
    if (matching.length === 0) {
      locked = true;
      status.textContent = 'این خانه انتخاب درستی نیست؛ حرکت را بررسی کن…';
      status.className = 'puzzle-instruction error is-wrong-preview';
      root.classList.add('puzzle-wrong-move');
      ground.set({
        animation: { enabled: true, duration: 320 },
        movable: { color: undefined, dests: new Map() }
      });
      ground.setShapes([{ orig: move.to, brush: 'red' }]);

      const responsePromise = recordAttempt(false, [...playedMoves, uci]);
      await delay(520);
      game.undo();
      refreshPlayableBoard(ground, game, false, [move.from, move.to]);
      await delay(340);
      ground.setShapes([]);
      ground.set({ animation: { enabled: true, duration: 180 } });
      refreshPlayableBoard(ground, game, true);
      root.classList.remove('puzzle-wrong-move');

      status.textContent = 'حرکت برگشت؛ بدون شروع دوباره می‌توانی راه دیگری را امتحان کنی.';
      status.className = 'puzzle-instruction error';
      const response = await responsePromise;
      showFailedResult(response);
      locked = false;
      updateTimelineButtons();
      return;
    }

    candidates = matching;
    activeAnswer = candidates[0];
    playedMoves.push(uci);
    cursor++;
    refreshPlayableBoard(ground, game, false, [move.from, move.to]);
    pushTimeline([move.from, move.to]);
    renderProgress();
    if (candidates.some(line => line.length === cursor)) {
      await finishSolved();
      return;
    }

    status.textContent = 'حرکت درست بود؛ پاسخ حریف…';
    status.className = 'puzzle-instruction thinking';
    locked = true;
    await delay(360);
    const replyOptions = [...new Set(candidates.map(line => line[cursor]).filter(Boolean))];
    const opponentUci = replyOptions[0];
    const reply = playUci(game, opponentUci);
    if (!reply) {
      status.textContent = 'ادامه ثبت‌شده معما معتبر نیست.';
      status.className = 'puzzle-instruction error';
      return;
    }

    candidates = candidates.filter(line => line[cursor] === opponentUci);
    activeAnswer = candidates[0];
    playedMoves.push(opponentUci);
    cursor++;
    refreshPlayableBoard(ground, game, false, [reply.from, reply.to]);
    pushTimeline([reply.from, reply.to]);
    renderProgress();
    await delay(180);
    if (candidates.some(line => line.length === cursor)) {
      await finishSolved();
      return;
    }

    locked = false;
    status.textContent = turnStatus();
    status.className = 'puzzle-instruction';
    refreshPlayableBoard(ground, game, true, [reply.from, reply.to]);
  }

  async function finishSolved() {
    locked = true;
    completed = true;
    if (timeline[timelineIndex]) timeline[timelineIndex].completed = true;
    refreshPlayableBoard(ground, game, false);
    const response = await recordAttempt(true, playedMoves);
    resultCard.hidden = false;
    resultCard.className = 'puzzle-result-card success';
    const delta = Number(response?.delta ?? 0);
    resultCard.innerHTML = `<strong>آفرین؛ معما حل شد!</strong><span>${response?.authenticated === false ? 'برای ثبت ریتینگ وارد حساب شو.' : response?.alreadyAttempted ? 'این معما قبلاً در ریتینگت محاسبه شده است.' : `تغییر ریتینگ: ${delta >= 0 ? '+' : ''}${delta}`}</span><button type="button" class="button button-small" data-result-next>معمای بعدی ←</button>`;
    resultCard.querySelector('[data-result-next]')?.addEventListener('click', () => loadNext(selectedCategory));
    status.textContent = 'یکی از مسیرهای صحیح را کامل پیدا کردی.';
    status.className = 'puzzle-instruction success';
    if (response?.rating && ratingElement) ratingElement.textContent = response.rating;
    if (response?.authenticated && !response?.alreadyAttempted && attemptsElement) {
      attemptsElement.textContent = String((Number(attemptsElement.textContent) || 0) + 1);
    }
    updateTimelineButtons();
    revealMobileFeedback();
  }

  function showFailedResult(response) {
    resultCard.hidden = false;
    resultCard.className = 'puzzle-result-card error';
    const delta = Number(response?.delta ?? 0);
    const ratingCopy = response?.authenticated === false
      ? 'برای ثبت نتیجه وارد حساب شو.'
      : response?.alreadyAttempted
        ? 'این معما قبلاً در ریتینگت محاسبه شده است.'
        : `تغییر ریتینگ: ${delta >= 0 ? '+' : ''}${delta}`;
    resultCard.innerHTML = `<strong>حرکت اشتباه بود؛ دوباره تلاش کن</strong><span>مهره به جای قبلی برگشت و همین پوزیسیون فعال است. ${ratingCopy}</span><div><button type="button" class="button button-small button-secondary" data-result-dismiss>ادامه تلاش</button><button type="button" class="button button-small button-ghost" data-result-next>معمای بعدی</button></div>`;
    resultCard.querySelector('[data-result-dismiss]')?.addEventListener('click', () => { resultCard.hidden = true; refreshPlayableBoard(ground, game, true); });
    resultCard.querySelector('[data-result-next]')?.addEventListener('click', () => loadNext(selectedCategory));
    if (response?.rating && ratingElement) ratingElement.textContent = response.rating;
    if (response?.authenticated && !response?.alreadyAttempted && attemptsElement) {
      attemptsElement.textContent = String((Number(attemptsElement.textContent) || 0) + 1);
    }
    revealMobileFeedback();
  }

  async function recordAttempt(solved, moves) {
    if (attemptRecorded) return lastAttemptResponse ?? { alreadyAttempted: true };
    attemptRecorded = true;
    const token = root.querySelector('input[name="__RequestVerificationToken"]')?.value;
    const data = new FormData();
    data.append('puzzleId', root.dataset.puzzleId);
    data.append('moves', moves.join(' '));
    data.append('failed', String(!solved));
    if (token) data.append('__RequestVerificationToken', token);
    try {
      const response = await fetch(root.dataset.submitUrl, { method: 'POST', body: data, credentials: 'same-origin' });
      lastAttemptResponse = response.ok ? await response.json() : null;
      return lastAttemptResponse;
    } catch {
      attemptRecorded = false;
      return null;
    }
  }

  function reset(options = {}) {
    game.load(initialFen);
    cursor = 0;
    if (options.newPuzzle) {
      attemptRecorded = false;
      lastAttemptResponse = null;
    }
    locked = false;
    completed = false;
    showingAnswer = false;
    playedMoves = [];
    candidates = cloneSolutions(solutionLines);
    activeAnswer = candidates[0] ?? [];
    puzzleOrientation = orientationFor(game);
    timeline = [];
    timelineIndex = -1;
    pushTimeline();
    status.textContent = turnStatus();
    status.className = 'puzzle-instruction';
    resultCard.hidden = true;
    resultCard.replaceChildren();
    renderProgress();
    ground.set({ orientation: puzzleOrientation });
    refreshPlayableBoard(ground, game, true);
    updateTimelineButtons();
  }

  async function showAnswer() {
    if (loading) return;
    locked = true;
    showingAnswer = true;
    completed = false;
    game.load(initialFen);
    cursor = 0;
    playedMoves = [];
    candidates = cloneSolutions(solutionLines);
    activeAnswer = solutionLines[0] ?? [];
    timeline = [];
    timelineIndex = -1;
    pushTimeline();
    ground.set({ orientation: puzzleOrientation });
    refreshPlayableBoard(ground, game, false);
    status.textContent = solutionLines.length > 1 ? `نمایش جواب اول از ${solutionLines.length} جواب صحیح…` : 'نمایش جواب…';
    status.className = 'puzzle-instruction thinking';
    for (const uci of activeAnswer) {
      const move = playUci(game, uci);
      if (!move) break;
      playedMoves.push(uci);
      cursor++;
      refreshPlayableBoard(ground, game, false, [move.from, move.to]);
      pushTimeline([move.from, move.to]);
      renderProgress();
      await delay(430);
    }
    status.textContent = 'جواب نمونه روی تخته نمایش داده شد.';
    status.className = 'puzzle-instruction warning';
    updateTimelineButtons();
    revealMobileFeedback();
  }

  async function loadNext(category, categoryChanged = false) {
    if (loading) return;
    setLoading(true);
    try {
      const url = new URL(root.dataset.nextUrl, window.location.href);
      if (category) url.searchParams.set('category', category);
      if (!categoryChanged && root.dataset.puzzleId) url.searchParams.set('excludeId', root.dataset.puzzleId);
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('دریافت معما انجام نشد.');
      const payload = await response.json();
      if (!payload.ok || !payload.puzzle) throw new Error(payload.message || 'معمای دیگری پیدا نشد.');
      applyPuzzle(payload.puzzle);
      if (payload.viewerRating && ratingElement) ratingElement.textContent = payload.viewerRating;
      if (Number.isFinite(payload.attemptedCount) && attemptsElement) attemptsElement.textContent = payload.attemptedCount;
      updateAddress(payload.puzzle);
    } catch (error) {
      status.textContent = error?.message || 'در دریافت معمای بعدی مشکلی پیش آمد.';
      status.className = 'puzzle-instruction error';
    } finally {
      setLoading(false);
    }
  }

  function applyPuzzle(puzzle) {
    initialFen = puzzle.fen;
    solutionLines = normalizeSolutions(puzzle.solutions);
    root.dataset.puzzleId = puzzle.id;
    root.dataset.fen = puzzle.fen;
    root.dataset.solutions = JSON.stringify(solutionLines);
    root.dataset.category = selectedCategory;
    root.querySelector('[data-puzzle-title]').textContent = 'بهترین حرکت را پیدا کن';
    root.querySelector('[data-puzzle-category-name]').textContent = puzzle.categoryName || 'معما';
    root.querySelector('[data-puzzle-item-rating]').textContent = puzzle.rating;
    renderSolutionCount();
    updateCategoryButtons();
    reset({ newPuzzle: true });
    boardElement.focus?.({ preventScroll: true });
  }

  function setLoading(value) {
    loading = value;
    loadingCover.hidden = !value;
    [nextButton, resetButton, answerButton, undoButton, redoButton, ...categoryButtons].forEach(button => { if (button) button.disabled = value; });
    root.classList.toggle('is-loading', value);
  }

  function updateCategoryButtons() {
    categoryButtons.forEach(button => button.classList.toggle('active', (button.dataset.puzzleCategory || '') === selectedCategory));
  }

  function updateAddress(puzzle) {
    const url = new URL(window.location.href);
    if (selectedCategory) url.searchParams.set('category', selectedCategory); else url.searchParams.delete('category');
    url.searchParams.set('puzzleId', puzzle.id);
    history.replaceState({ puzzleId: puzzle.id }, '', url);
  }

  function revealMobileFeedback() {}

  function renderReadyState() {
    renderSolutionCount();
    reset({ newPuzzle: true });
  }

  function renderSolutionCount() {
    const count = root.querySelector('[data-puzzle-solution-count]');
    if (!count) return;
    count.textContent = `${solutionLines.length} مسیر`;
    count.title = solutionLines.length > 1 ? 'این معما ممکن است چند جواب صحیح داشته باشد' : '';
  }

  function pushTimeline(lastMove = null, isCompleted = false) {
    if (timelineIndex < timeline.length - 1) timeline = timeline.slice(0, timelineIndex + 1);
    timeline.push({
      fen: game.fen(),
      cursor,
      playedMoves: [...playedMoves],
      candidates: cloneSolutions(candidates),
      activeAnswer: [...(activeAnswer ?? [])],
      lastMove,
      completed: isCompleted
    });
    timelineIndex = timeline.length - 1;
    updateTimelineButtons();
  }

  function navigateTimeline(step) {
    if (loading || timeline.length === 0) return;
    const nextIndex = Math.max(0, Math.min(timeline.length - 1, timelineIndex + step));
    if (nextIndex === timelineIndex) return;
    timelineIndex = nextIndex;
    const snapshot = timeline[timelineIndex];
    game.load(snapshot.fen);
    cursor = snapshot.cursor;
    playedMoves = [...snapshot.playedMoves];
    candidates = cloneSolutions(snapshot.candidates);
    activeAnswer = [...snapshot.activeAnswer];
    const atLatest = timelineIndex === timeline.length - 1;
    const canContinue = atLatest && !completed && !showingAnswer;
    locked = !canContinue;
    refreshPlayableBoard(ground, game, canContinue, snapshot.lastMove);
    renderProgress();
    const totalMoves = Math.ceil((timeline.length - 1) / 2);
    const mover = game.turn() === 'w' ? 'سیاه' : 'سفید';
    status.textContent = atLatest
      ? (completed ? 'پایان مسیر؛ می‌توانی حرکت‌ها را مرور کنی.' : showingAnswer ? 'در حال مرور جواب نمونه.' : turnStatus())
      : timelineIndex === 0
        ? `موقعیت آغازین · ${totalMoves.toLocaleString('fa-IR')} حرکت`
        : `مرور حرکت ${Math.ceil(timelineIndex / 2).toLocaleString('fa-IR')} از ${totalMoves.toLocaleString('fa-IR')} · ${mover}`;
    status.className = atLatest ? 'puzzle-instruction' : 'puzzle-instruction warning';
    updateTimelineButtons();
  }

  function updateTimelineButtons() {
    if (undoButton) undoButton.disabled = loading || timelineIndex <= 0;
    if (redoButton) redoButton.disabled = loading || timelineIndex >= timeline.length - 1;
  }

  function renderProgress() {
    progress.replaceChildren();
    const length = Math.max(1, activeAnswer?.length ?? 0);
    for (let index = 0; index < length; index++) {
      const dot = document.createElement('span');
      dot.className = index < cursor ? 'done' : index === cursor ? 'current' : '';
      progress.append(dot);
    }
  }
}

function readSolutionLines(value) {
  try { return normalizeSolutions(JSON.parse(value || '[]')); }
  catch { return []; }
}
function normalizeSolutions(value) {
  return Array.isArray(value)
    ? value.map(line => Array.isArray(line) ? line.map(x => String(x).toLowerCase()) : []).filter(x => x.length)
    : [];
}
function cloneSolutions(lines) { return lines.map(line => [...line]); }
function playUci(game, uci) { return game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || 'q' }); }
function toUci(move) { return `${move.from}${move.to}${move.promotion ?? ''}`.toLowerCase(); }
function orientationFor(game) { return game.turn() === 'w' ? 'white' : 'black'; }
function delay(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }
